import { useEffect, useState } from 'react';
import { desktopAppPathForNavigation, isClientRoutePath, stripDesktopAppBasePath } from './desktopPath';

// Lightweight global client router — gives the whole app instant pushState
// navigation (no full page reloads) without pulling in react-router. It works
// by (1) patching history.pushState/replaceState to emit a popstate event so
// any component can react to URL changes, and (2) intercepting clicks on
// same-origin <a> links and routing them through history instead of the browser
// load. RepoView already listens to popstate for its in-repo routing, so this
// keeps it in sync for free.
//
// Escape hatches: external links, target/_blank, download, rel="external",
// data-no-router, and modified clicks (cmd/ctrl/shift/alt, middle-click) all
// fall through to a normal browser navigation.

let installed = false;

function patchHistory(): void {
  for (const key of ['pushState', 'replaceState'] as const) {
    const original = history[key];
    if ((original as any).__patched) continue;
    const patched = function (this: History, ...args: any[]) {
      const result = original.apply(this, args as any);
      // Notify listeners (our hook + RepoView) that the location changed.
      window.dispatchEvent(new PopStateEvent('popstate', { state: history.state }));
      return result;
    } as typeof history.pushState;
    (patched as any).__patched = true;
    history[key] = patched;
  }
}

// navigate performs a client-side navigation. Same-origin only; anything else
// (or an explicit hard flag) falls back to a full browser load.
export function navigate(to: string, opts: { replace?: boolean; hard?: boolean } = {}): void {
  if (typeof window === 'undefined') return;
  const url = new URL(to, window.location.href);
  if (opts.hard || url.origin !== window.location.origin) {
    window.location.href = to;
    return;
  }
  const pathname = desktopAppPathForNavigation(url.pathname, window.location.pathname);
  const dest = pathname + url.search + url.hash;
  const samePage = pathname === window.location.pathname && url.search === window.location.search;
  if (opts.replace) history.replaceState({}, '', dest);
  else history.pushState({}, '', dest);
  if (samePage && url.hash) {
    document.getElementById(decodeURIComponent(url.hash.slice(1)))?.scrollIntoView({ behavior: 'smooth' });
  } else {
    window.scrollTo(0, 0);
  }
}

function onDocumentClick(e: MouseEvent): void {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const target = e.target as HTMLElement | null;
  const anchor = target?.closest?.('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (
    !href ||
    href.startsWith('#') ||
    anchor.target === '_blank' ||
    anchor.hasAttribute('download') ||
    anchor.getAttribute('rel') === 'external' ||
    anchor.dataset.noRouter !== undefined
  ) {
    return;
  }
  const url = new URL(href, window.location.href);
  if (url.origin !== window.location.origin) return; // external — let the browser handle it
  if (!isClientRoutePath(url.pathname)) return;
  e.preventDefault();
  navigate(url.pathname + url.search + url.hash);
}

// installRouter wires the history patch + the global click interceptor exactly
// once. Safe to call from multiple components / StrictMode double-invokes.
export function installRouter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  patchHistory();
  document.addEventListener('click', onDocumentClick);
}

// useLocationPath returns the current pathname and re-renders on any client
// navigation (pushState, replaceState, or back/forward). On the server it
// returns the provided initial path so SSR/prerender output is stable.
export function useLocationPath(initial?: string): string {
  const [path, setPath] = useState(
    () => stripDesktopAppBasePath(initial ?? (typeof window === 'undefined' ? '/' : window.location.pathname)),
  );
  useEffect(() => {
    const update = () => setPath(stripDesktopAppBasePath(window.location.pathname));
    update(); // sync once on mount in case the URL changed before hydration
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  return path;
}
